import { useState, useEffect } from "react";
import API, { formatError } from "@/api";
import { toDateTimeLocal } from "@/lib/utils";
import { toast } from "sonner";
import UserAvatar from "@/components/UserAvatar";
import { X, Loader2, Search } from "lucide-react";

const PRIORITY_OPTIONS = [
    { value: "low", label: "Scăzut" },
    { value: "medium", label: "Mediu" },
    { value: "high", label: "Ridicat" },
    { value: "critical", label: "Critic" },
];

export default function CreateTaskModal({ boardId, columnId, columnKey, teamId, onClose, onCreated }) {
    const isRecurrent = columnKey === "recurrent";
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [assigneeIds, setAssigneeIds] = useState([]);
    const [priority, setPriority] = useState("medium");
    const [dueDate, setDueDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [assigneeSearch, setAssigneeSearch] = useState("");
    // Recurență (doar pentru coloana Recurent)
    const [recFreq, setRecFreq] = useState("weekly");
    const [recInterval, setRecInterval] = useState(1);
    const [recStart, setRecStart] = useState(() => toDateTimeLocal(new Date().toISOString()));
    const [recSkipIfOpen, setRecSkipIfOpen] = useState(false);

    useEffect(() => {
        API.get("/users").then(r => setUsers(r.data)).catch(() => {});
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) { toast.error("Titlul este obligatoriu"); return; }
        setLoading(true);
        try {
            const payload = {
                board_id: boardId,
                column_id: columnId,
                team_id: teamId,
                title: title.trim(),
                description,
                assignee_ids: assigneeIds,
                priority,
                due_date: isRecurrent ? null : (dueDate ? new Date(dueDate).toISOString() : null),
            };
            if (isRecurrent) {
                payload.recurrence = {
                    freq: recFreq,
                    interval: Math.max(1, parseInt(recInterval, 10) || 1),
                    skip_if_open: recSkipIfOpen,
                    next_run: recStart ? new Date(recStart).toISOString() : new Date().toISOString(),
                };
            }
            await API.post("/tasks", payload);
            toast.success(isRecurrent ? "Task recurent creat" : "Task creat cu succes");
            onCreated();
            onClose();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative glass-panel rounded-2xl shadow-xl w-full max-w-md mx-4 z-10" data-testid="create-task-modal">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#e3e7f5]">
                    <h2 className="font-bold text-[#111827] text-base" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{isRecurrent ? "Task recurent nou" : "Task nou"}</h2>
                    <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827] transition-colors">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Titlu *</label>
                        <input
                            data-testid="new-task-title"
                            autoFocus
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="Ce trebuie făcut?"
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                        <textarea
                            data-testid="new-task-description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Detalii opționale..."
                            rows={3}
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Responsabili</label>
                            <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border border-[#E4E4E7] rounded-md min-h-[36px] bg-white">
                                {assigneeIds.length === 0 && <span className="text-xs text-[#9CA3AF]">Click pe nume pentru a atribui...</span>}
                                {assigneeIds.map(id => {
                                    const u = users.find(x => x.id === id);
                                    if (!u) return null;
                                    return (
                                        <span key={id} className="inline-flex items-center gap-1 bg-blue-50 text-[#002FA7] rounded-full pl-1 pr-1.5 py-0.5 text-xs">
                                            <UserAvatar user={u} className="w-4 h-4" textClassName="text-[8px]" />
                                            {u.name}
                                            <button type="button" onClick={() => setAssigneeIds(assigneeIds.filter(x => x !== id))} className="ml-0.5 hover:text-red-500">×</button>
                                        </span>
                                    );
                                })}
                            </div>
                            <div className="max-h-32 overflow-y-auto mt-1 border border-[#F1F3F5] rounded">
                                <div className="sticky top-0 bg-white border-b border-[#F1F3F5] px-1.5 py-1">
                                    <div className="relative">
                                        <Search size={11} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                        <input
                                            data-testid="new-task-assignee-search"
                                            type="text"
                                            value={assigneeSearch}
                                            onChange={e => setAssigneeSearch(e.target.value)}
                                            placeholder="Caută utilizator..."
                                            className="w-full pl-6 pr-2 py-1 text-xs border border-[#E4E4E7] rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7] bg-white"
                                        />
                                    </div>
                                </div>
                                <div className="p-1">
                                    {(() => {
                                        const q = assigneeSearch.trim().toLowerCase();
                                        const filtered = users
                                            .filter(u => !assigneeIds.includes(u.id))
                                            .filter(u => {
                                                if (!q) return true;
                                                return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                                            });
                                        if (filtered.length === 0) {
                                            return <div className="px-2 py-1.5 text-[11px] text-[#9CA3AF] text-center">{q ? "Niciun rezultat" : "Toți utilizatorii sunt deja atribuiți"}</div>;
                                        }
                                        return filtered.map(u => (
                                            <button key={u.id} type="button"
                                                data-testid={`new-task-assignee-${u.id}`}
                                                onClick={() => setAssigneeIds([...assigneeIds, u.id])}
                                                className="w-full flex items-center gap-2 text-xs hover:bg-[#F8F9FA] px-2 py-1 rounded text-left">
                                                <UserAvatar user={u} className="w-5 h-5" textClassName="text-[9px]" />
                                                <span className="truncate">{u.name}</span>
                                                <span className="ml-auto text-[10px] text-[#9CA3AF] truncate">{u.email}</span>
                                            </button>
                                        ));
                                    })()}
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Prioritate</label>
                            <select
                                data-testid="new-task-priority"
                                value={priority}
                                onChange={e => setPriority(e.target.value)}
                                className="w-full px-2 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                            >
                                {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>
                    {!isRecurrent ? (
                        <div>
                            <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Termen limită</label>
                            <input
                                data-testid="new-task-due-date"
                                type="datetime-local"
                                value={dueDate}
                                onChange={e => setDueDate(e.target.value)}
                                className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            />
                        </div>
                    ) : (
                        <div className="rounded-md border border-[#BAE6FD] bg-[#F0F9FF] p-3 space-y-3">
                            <p className="text-xs font-bold text-[#0369A1] uppercase tracking-wide">Recurență</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Frecvență</label>
                                    <select
                                        data-testid="rec-freq"
                                        value={recFreq}
                                        onChange={e => setRecFreq(e.target.value)}
                                        className="w-full px-2 py-2 border border-[#BAE6FD] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] bg-white"
                                    >
                                        <option value="daily">Zilnic</option>
                                        <option value="weekly">Săptămânal</option>
                                        <option value="monthly">Lunar</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">La fiecare</label>
                                    <input
                                        data-testid="rec-interval"
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={recInterval}
                                        onChange={e => setRecInterval(e.target.value)}
                                        className="w-full px-3 py-2 border border-[#BAE6FD] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] bg-white"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Prima generare (data de start)</label>
                                <input
                                    data-testid="rec-start"
                                    type="datetime-local"
                                    value={recStart}
                                    onChange={e => setRecStart(e.target.value)}
                                    className="w-full px-3 py-2 border border-[#BAE6FD] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] bg-white"
                                />
                            </div>
                            <label className="flex items-start gap-2 text-xs text-[#4B5563] cursor-pointer select-none">
                                <input
                                    data-testid="rec-skip-if-open"
                                    type="checkbox"
                                    checked={recSkipIfOpen}
                                    onChange={e => setRecSkipIfOpen(e.target.checked)}
                                    className="w-4 h-4 mt-0.5 accent-[#0EA5E9]"
                                />
                                <span>Nu genera o instanță nouă dacă cea precedentă nu e încă în „Finalizat”.</span>
                            </label>
                            <p className="text-[11px] text-[#0369A1] leading-snug">
                                La fiecare interval, se creează automat o copie a acestui task în „De făcut”. Mută masterul în „Finalizat” pentru a opri seria.
                            </p>
                        </div>
                    )}
                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA] transition-colors">
                            Anulează
                        </button>
                        <button
                            data-testid="create-task-submit-btn"
                            type="submit"
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] transition-colors disabled:opacity-60"
                        >
                            {loading && <Loader2 size={14} className="animate-spin" />}
                            {isRecurrent ? "Creează task recurent" : "Creează task"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
